import React, { useEffect, useState } from 'react';
import { useSearchParams, Link } from 'react-router-dom';
import Layout from '../components/Layout';
import { CheckCircle2, Loader2, AlertTriangle } from 'lucide-react';
import api from '../lib/api';

const money = (pence, ccy = 'gbp') => {
  const sym = { gbp: '£', usd: '$', eur: '€' }[ccy] || '';
  return `${sym}${((pence || 0) / 100).toFixed(2)}`;
};

/**
 * Post-Checkout landing that polls /tips/status until the webhook flips the tx to paid.
 * Times out after 12 attempts (~24s) so a stalled webhook doesn't spin forever.
 */
export default function TipSuccess() {
  const [params] = useSearchParams();
  const sessionId = params.get('session_id');
  const [state, setState] = useState({ status: 'polling', tx: null });

  useEffect(() => {
    if (!sessionId) { setState({ status: 'unknown', tx: null }); return; }
    let attempts = 0;
    let cancelled = false;
    const tick = async () => {
      attempts += 1;
      try {
        const { data } = await api.get(`/tips/status/${sessionId}`);
        if (cancelled) return;
        if (data.payment_status === 'paid') { setState({ status: 'paid', tx: data }); return; }
        if (data.payment_status === 'failed' || data.payment_status === 'expired') { setState({ status: 'failed', tx: data }); return; }
        if (attempts >= 12) { setState({ status: 'timeout', tx: data }); return; }
        setTimeout(tick, 2000);
      } catch {
        if (attempts >= 3) setState({ status: 'error', tx: null });
        else setTimeout(tick, 2000);
      }
    };
    tick();
    return () => { cancelled = true; };
  }, [sessionId]);

  return (
    <Layout>
      <div className="max-w-md mx-auto py-16 text-center" data-testid="tip-success-page">
        {state.status === 'polling' && (
          <>
            <Loader2 size={48} className="mx-auto text-[#FFB000] animate-spin mb-4" />
            <h1 className="font-heading font-black text-white text-3xl mb-2">Settling your tip…</h1>
            <p className="text-[#A1A1AA] text-sm">Stripe's confirming the payment. This usually takes a couple of seconds.</p>
          </>
        )}
        {state.status === 'paid' && (
          <>
            <CheckCircle2 size={64} className="mx-auto text-[#22C55E] mb-4" />
            <h1 className="font-heading font-black text-white text-3xl mb-2">Tip sent!</h1>
            <p className="text-[#A1A1AA] text-sm mb-6">
              {state.tx?.amount ? money(state.tx.amount, state.tx.currency) : 'Your tip'} is on its way to the DJ. Thanks for keeping the decks spinning.
            </p>
            <Link to="/feed" className="btn-pill btn-primary" data-testid="tip-success-back">Back to feed</Link>
          </>
        )}
        {state.status === 'unknown' && (
          <>
            <AlertTriangle size={48} className="mx-auto text-[#FFB000] mb-4" />
            <h1 className="font-heading font-black text-white text-3xl mb-2">Checkout session not found</h1>
            <p className="text-[#A1A1AA] text-sm mb-6">We can't tell what this URL is for. If you were tipping, it may already have gone through — check the DJ's profile.</p>
            <Link to="/feed" className="btn-pill btn-outline" data-testid="tip-success-back">Back to feed</Link>
          </>
        )}
        {(state.status === 'failed' || state.status === 'error') && (
          <>
            <AlertTriangle size={48} className="mx-auto text-[#EF4444] mb-4" />
            <h1 className="font-heading font-black text-white text-3xl mb-2">Tip didn't go through</h1>
            <p className="text-[#A1A1AA] text-sm mb-6">You weren't charged. Give it another go — Stripe test cards work here.</p>
            <Link to="/feed" className="btn-pill btn-outline" data-testid="tip-success-back">Back to feed</Link>
          </>
        )}
        {state.status === 'timeout' && (
          <>
            <Loader2 size={48} className="mx-auto text-[#FFB000] mb-4" />
            <h1 className="font-heading font-black text-white text-3xl mb-2">Still processing</h1>
            <p className="text-[#A1A1AA] text-sm mb-6">Stripe is taking a bit longer than usual. The tip will appear in the DJ's payouts once it clears.</p>
            <Link to="/feed" className="btn-pill btn-outline" data-testid="tip-success-back">Back to feed</Link>
          </>
        )}
      </div>
    </Layout>
  );
}
